import React, { useState, useMemo } from 'react';
import { Due } from '../types';
import { EditIcon, DeleteIcon, DuesIcon } from './icons';
import { formatCurrency } from '../utils/formatting';
import ConfirmationModal from './ConfirmationModal';

interface DuesViewProps {
    dues: Due[];
    onEdit: (due: Due) => void;
    onDelete: (id: string) => void;
    currency: string;
}

const DueCard: React.FC<{ due: Due, onEdit: () => void, onDelete: () => void, currency: string }> = ({ due, onEdit, onDelete, currency }) => {
    const isCompleted = due.status === 'completed' || (due.paymentType === 'installments' && due.installmentsPaid >= due.installmentsTotal);
    
    const paymentAmount = useMemo(() => {
        if (due.paymentType === 'single') return due.totalAmount;
        if (due.installmentsTotal > 0) return due.totalAmount / due.installmentsTotal;
        return 0;
    }, [due]);

    const nextPaymentDate = useMemo(() => {
        if (isCompleted) return null;
        const startDate = new Date(due.startDate);
        let nextPaymentMonth = startDate.getMonth() + due.installmentsPaid;
        let nextPaymentYear = startDate.getFullYear();
        
        if (nextPaymentMonth >= 12) {
            nextPaymentYear += Math.floor(nextPaymentMonth / 12);
            nextPaymentMonth = nextPaymentMonth % 12;
        }
        
        return new Date(nextPaymentYear, nextPaymentMonth, due.paymentDayOfMonth);
    }, [due, isCompleted]);

    const progressPercentage = due.paymentType === 'installments' && due.installmentsTotal > 0
        ? (due.installmentsPaid / due.installmentsTotal) * 100
        : (isCompleted ? 100 : 0);

    return (
        <div className={`bg-light-surface dark:bg-surface p-5 rounded-lg border border-light-border dark:border-border shadow-md flex flex-col justify-between ${isCompleted ? 'opacity-60' : ''}`}>
            <div>
                <div className="flex justify-between items-start">
                    <div>
                        <h4 className="font-bold text-lg text-light-text-primary dark:text-text-primary">{due.name}</h4>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-light-text-secondary dark:text-text-secondary">{due.category}</span>
                    </div>
                    <div className="flex gap-1">
                        <button onClick={onEdit} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-primary transition-colors" title="Editar"><EditIcon className="w-5 h-5"/></button>
                        <button onClick={onDelete} className="p-2 text-light-text-secondary dark:text-text-secondary hover:text-red-500 transition-colors" title="Eliminar"><DeleteIcon className="w-5 h-5"/></button>
                    </div>
                </div>

                <div className="my-4">
                    <p className="text-3xl font-bold text-red-500">{formatCurrency(paymentAmount, currency)}</p>
                    {nextPaymentDate && (
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary">
                            Próximo pago: {nextPaymentDate.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                    )}
                </div>

                {due.paymentType === 'installments' && (
                    <div>
                        <div className="flex justify-between text-sm font-medium text-light-text-secondary dark:text-text-secondary mb-1">
                            <span>Progreso</span>
                            <span>Pagado {due.installmentsPaid} de {due.installmentsTotal}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                            <div className="bg-secondary h-2.5 rounded-full" style={{ width: `${progressPercentage}%` }}></div>
                        </div>
                    </div>
                )}
            </div>
            {isCompleted && (
                <div className="mt-4 pt-4 border-t border-light-border dark:border-border text-center text-secondary font-semibold">
                    Completado
                </div>
            )}
        </div>
    );
};

const DuesView: React.FC<DuesViewProps> = ({ dues, onEdit, onDelete, currency }) => {
    const [filter, setFilter] = useState<'active' | 'completed'>('active');
    const [deletingDue, setDeletingDue] = useState<Due | null>(null);

    const filteredDues = useMemo(() => {
        return dues
            .filter(due => {
                const isCompleted = due.status === 'completed' || (due.paymentType === 'installments' && due.installmentsPaid >= due.installmentsTotal);
                return filter === 'active' ? !isCompleted : isCompleted;
            })
            .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    }, [dues, filter]);
    
    const handleConfirmDelete = () => {
        if(deletingDue) {
            onDelete(deletingDue.id);
            setDeletingDue(null);
        }
    };

    return (
        <>
            <ConfirmationModal
                isOpen={!!deletingDue}
                onClose={() => setDeletingDue(null)}
                onConfirm={handleConfirmDelete}
                title="Confirmar Eliminación"
                message={`¿Estás seguro de que quieres eliminar el vencimiento "${deletingDue?.name}"? Esta acción no se puede deshacer.`}
                confirmText="Eliminar"
            />
            <div className="space-y-6">
                <div className="bg-light-surface dark:bg-surface p-4 sm:p-6 rounded-lg border border-light-border dark:border-border shadow-md">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                        <div>
                            <h3 className="font-bold text-xl text-light-text-primary dark:text-text-primary">Gestión de Vencimientos</h3>
                            <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">Controla tus pagos recurrentes, créditos e hipotecas.</p>
                        </div>
                        <div className="flex gap-2 rounded-lg bg-gray-200 dark:bg-gray-800 p-1 mt-4 sm:mt-0">
                            <button onClick={() => setFilter('active')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'active' ? 'bg-primary text-white shadow' : 'text-light-text-secondary dark:text-text-secondary'}`}>
                                Activos
                            </button>
                            <button onClick={() => setFilter('completed')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${filter === 'completed' ? 'bg-primary text-white shadow' : 'text-light-text-secondary dark:text-text-secondary'}`}>
                                Completados
                            </button>
                        </div>
                    </div>
                </div>

                {filteredDues.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredDues.map(due => (
                            <DueCard 
                                key={due.id} 
                                due={due} 
                                onEdit={() => onEdit(due)} 
                                onDelete={() => setDeletingDue(due)} 
                                currency={currency}
                            />
                        ))}
                    </div>
                ) : (
                     <div className="text-center py-16 bg-light-surface dark:bg-surface rounded-lg border border-light-border dark:border-border">
                        <DuesIcon className="w-16 h-16 text-light-text-secondary dark:text-text-secondary mx-auto mb-4"/>
                        <h4 className="font-bold text-lg text-light-text-primary dark:text-text-primary">No hay vencimientos {filter === 'active' ? 'activos' : 'completados'}.</h4>
                        <p className="text-sm text-light-text-secondary dark:text-text-secondary mt-1">
                            {filter === 'active' 
                                ? 'Añade un nuevo vencimiento para empezar a hacer seguimiento.' 
                                : 'Aquí aparecerán los vencimientos una vez finalizados.'}
                        </p>
                    </div>
                )}
            </div>
        </>
    );
};

export default DuesView;
